import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSupportTicketMessageThread } from "@/components/HubSupportTicketMessageThread";
import { ApiError } from "@/lib/api";
import {
  addQcIncidentMessage,
  fetchQcIncidentMessages,
} from "@/pages/manufacturing/qcIncidentsApi";

export function QcIncidentNotesThread({
  incidentId,
  writesAllowed,
}: {
  incidentId: number;
  writesAllowed: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const queryKey = ["qc-incident-messages", incidentId] as const;
  const messagesQuery = useQuery({
    queryKey,
    queryFn: () => fetchQcIncidentMessages(incidentId),
  });
  const addMutation = useMutation({
    mutationFn: (body: string) => addQcIncidentMessage(incidentId, body),
    onSuccess: async (result) => {
      setDraft("");
      queryClient.setQueryData(queryKey, result);
    },
  });
  const error =
    addMutation.error instanceof ApiError
      ? addMutation.error.message
      : addMutation.error instanceof Error
        ? addMutation.error.message
        : messagesQuery.error instanceof Error
          ? messagesQuery.error.message
          : null;

  return (
    <HubSupportTicketMessageThread
      messages={(messagesQuery.data?.messages ?? []).map((message) => ({
        id: message.id,
        created_at: message.createdAt,
        author_user_id: message.authorUserId,
        author_email: message.authorEmail,
        author_display_name: message.authorDisplayName,
        body: message.body,
        is_from_reporter: true,
      }))}
      draft={draft}
      onDraftChange={setDraft}
      onSubmit={() => {
        addMutation.mutate(draft.trim());
      }}
      submitting={addMutation.isPending}
      canReply={writesAllowed}
      error={error}
      composeLabel="Add note"
      viewer="admin"
      sectionLabel="Notes"
      emptyMessage="No notes yet. Add a note while this incident is still open."
      placeholder="Leave a note for others working this incident…"
    />
  );
}
